import { create } from "zustand";
import { persist } from "zustand/middleware";
import { translations } from "../locales/translations";
import { Language, Translation } from "../types/i18n";
import { getLocaleString } from "../utils/dateLocalization";
import { log } from "@/lib/log";

/** English is the default and the fallback for any key a language lacks. */
function translationFor(language: Language): Translation {
  return { ...translations.en, ...(translations[language] ?? {}) };
}

/**
 * Native controls the browser draws itself — the date picker above all —
 * follow the document language, so <html lang> tracks the app language as a
 * full locale ("en-US", not "en") for US date order in English.
 */
function applyDocumentLanguage(language: Language): void {
  if (typeof document !== "undefined") {
    document.documentElement.lang = getLocaleString(language);
  }
}

interface I18nStore {
  language: Language;
  t: Translation;
  setLanguage: (language: Language) => void;
  resetToEnglish: () => void;
  getLanguageFlag: (language: Language) => string;
  getLanguageName: (language: Language) => string;
}

const languageFlags = {
  en: "🇺🇸",
  de: "🇩🇪",
  fr: "🇫🇷",
  es: "🇪🇸",
  pt: "🇵🇹",
};

const languageNames = {
  en: "English",
  de: "Deutsch",
  fr: "Français",
  es: "Español",
  pt: "Português",
};

export const useI18nStore = create<I18nStore>()(
  persist(
    (set) => ({
      language: "en",
      t: translations.en,
      setLanguage: (language: Language) => {
        log.debug("Setting language to:", language); // Debug log
        applyDocumentLanguage(language);
        set({ language, t: translationFor(language) });
      },
      resetToEnglish: () => {
        log.debug("Resetting to English"); // Debug log
        applyDocumentLanguage("en");
        set({
          language: "en",
          t: translations.en,
        });
      },
      getLanguageFlag: (language: Language) => languageFlags[language],
      getLanguageName: (language: Language) => languageNames[language],
    }),
    {
      name: "healthpass-language",
      partialize: (state) => ({ language: state.language }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          log.debug("Rehydrated language:", state.language); // Debug log
          // Ensure t is set correctly after rehydration
          state.t = translationFor(state.language);
          applyDocumentLanguage(state.language);
        }
      },
    },
  ),
);

// localStorage rehydrates synchronously inside create(); this covers the case
// where nothing was persisted yet.
applyDocumentLanguage(useI18nStore.getState().language);

// HMR support intentionally disabled here to keep the store simple and predictable.
